import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * @typedef {object} StickyAtcRefs
 * @property {HTMLElement} stickyBlock - The sticky ATC block element
 */

/**
 * A custom element that manages the sticky Add to Cart block visibility
 * Shows the block when buy buttons scroll out of view (only if buy buttons are above the sticky block)
 *
 * @extends {Component<StickyAtcRefs>}
 */
class StickyAtcComponent extends Component {
  #buyButtonsSelector = '.product-information .buy-buttons-block';
  #mobileBreakpoint = 750;
  #isMobile = () => window.innerWidth < this.#mobileBreakpoint;
  #ticking = false;
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Only initialize on mobile
    if (!this.#isMobile()) {
      return;
    }

    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Initialize the sticky ATC functionality
   */
  #init() {
    const buyButtonsBlock = document.querySelector(this.#buyButtonsSelector);

    if (!buyButtonsBlock) {
      return;
    }

    // Check if buy buttons are positioned above the sticky block in the DOM
    if (!this.#isBuyButtonsAbove(buyButtonsBlock)) {
      return;
    }

    // Initial check
    this.#checkVisibility(buyButtonsBlock);

    // Listen to scroll events
    window.addEventListener('scroll', () => this.#checkVisibility(buyButtonsBlock), { passive: true });

    // Listen to resize events
    const handleResize = debounce(() => {
      if (!this.#isMobile()) {
        this.#hide();
      } else {
        this.#checkVisibility(buyButtonsBlock);
      }
    }, 150);

    window.addEventListener('resize', handleResize, { passive: true });

    // Observe buy buttons block for position changes
    this.#resizeObserver = new ResizeObserver(() => {
      this.#checkVisibility(buyButtonsBlock);
    });
    this.#resizeObserver.observe(buyButtonsBlock);
  }

  /**
   * Cleanup event listeners and observers
   */
  #cleanup() {
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
  }

  /**
   * Check if buy buttons block is positioned above the sticky block in the DOM
   * @param {HTMLElement} buyButtonsBlock - The buy buttons block element
   * @returns {boolean} True if buy buttons are above the sticky block
   */
  #isBuyButtonsAbove(buyButtonsBlock) {
    const stickyBlock = this;

    // Use compareDocumentPosition to check relative position
    // If stickyBlock follows buyButtonsBlock, buy buttons are above
    const position = buyButtonsBlock.compareDocumentPosition(stickyBlock);
    const isFollowing = (position & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;

    return isFollowing;
  }

  /**
   * Check visibility of buy buttons and toggle sticky block accordingly
   * @param {HTMLElement} buyButtonsBlock - The buy buttons block element
   */
  #checkVisibility(buyButtonsBlock) {
    if (this.#ticking) return;
    this.#ticking = true;

    requestAnimationFrame(() => {
      const buyButtonsRect = buyButtonsBlock.getBoundingClientRect();
      const isBuyButtonsVisible = buyButtonsRect.top >= 0 && buyButtonsRect.bottom <= window.innerHeight;

      if (isBuyButtonsVisible) {
        this.#hide();
      } else {
        this.#show();
      }

      this.#ticking = false;
    });
  }

  /**
   * Show the sticky block
   */
  #show() {
    this.classList.add('sticky-atc-block--visible');
  }

  /**
   * Hide the sticky block
   */
  #hide() {
    this.classList.remove('sticky-atc-block--visible');
  }
}

if (!customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyAtcComponent);
}
